<template>
  <section class="card">
    <header class="head">
      <h2>权限管理（RBAC）</h2>
      <p class="sub">管理后台用户、角色与权限点。</p>
    </header>
    <p v-if="!canManage" class="warn">当前账号无权限：需要 rbac.manage 或超级管理员（*）</p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="grid">
      <div class="panel">
        <div class="panel-head">
          <h3>角色</h3>
          <button class="btn btn-primary" :disabled="!canManage" @click="openRoleCreate">新增角色</button>
        </div>
        <table class="rbac-table">
          <thead><tr><th>编码</th><th>名称</th><th>状态</th><th>权限数</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="r in roles" :key="r.id">
              <td>{{ r.roleCode || `ROLE#${r.id}` }}</td>
              <td>{{ r.roleName }}</td>
              <td class="col-status">
                <label
                  class="status-switch"
                  :class="{
                    on: isStatusEnabled(r.statusCode),
                    disabled: roleStatusSwitchDisabled(r),
                  }"
                  :title="isProtectedRole(r) ? '超级管理员角色不可停用' : ''"
                >
                  <input
                    type="checkbox"
                    class="status-switch-input"
                    :checked="isStatusEnabled(r.statusCode)"
                    :disabled="roleStatusSwitchDisabled(r)"
                    @change="onRoleStatusChange(r, $event)"
                  />
                  <span class="status-switch-track" aria-hidden="true" />
                </label>
              </td>
              <td>{{ (r.permissions || []).length }}</td>
              <td>
                <button class="btn btn-edit" :disabled="!canManage" @click="openRoleEdit(r)">编辑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="panel">
        <div class="panel-head">
          <h3>后台用户</h3>
          <button class="btn btn-primary" :disabled="!canManage" @click="openUserCreate">新增用户</button>
        </div>
        <table class="rbac-table">
          <thead><tr><th>用户名</th><th>显示名</th><th>手机号</th><th>专员类型</th><th>状态</th><th>角色</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="u in users" :key="u.id">
              <td>{{ u.username }}</td>
              <td>{{ u.displayName }}</td>
              <td>{{ u.bindMobile || '-' }}</td>
              <td>{{ staffScopeLabel(u.staffScopeCode) }}</td>
              <td class="col-status">
                <label
                  class="status-switch"
                  :class="{
                    on: isStatusEnabled(u.statusCode),
                    disabled: userStatusSwitchDisabled(u),
                  }"
                  :title="isProtectedUser(u) ? '系统管理员账号不可停用' : ''"
                >
                  <input
                    type="checkbox"
                    class="status-switch-input"
                    :checked="isStatusEnabled(u.statusCode)"
                    :disabled="userStatusSwitchDisabled(u)"
                    @change="onUserStatusChange(u, $event)"
                  />
                  <span class="status-switch-track" aria-hidden="true" />
                </label>
              </td>
              <td>{{ userRoleNames(u).join(', ') || '-' }}</td>
              <td class="col-actions">
                <button class="btn btn-edit" :disabled="!canManage" @click="openUserEdit(u)">编辑</button>
                <button
                  class="btn btn-delete"
                  :disabled="!canDeleteUser(u)"
                  :title="deleteUserDisabledReason(u)"
                  @click="onDeleteUser(u)"
                >
                  删除
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showRoleDialog" class="mask">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ roleEditId ? '编辑角色' : '新增角色' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showRoleDialog = false">×</button>
        </div>
        <div class="dialog-body">
        <div class="form">
          <label class="field"><span>角色编码（可空）</span><input v-model.trim="roleForm.roleCode" :disabled="!!roleEditId" placeholder="可不填，系统自动生成"></label>
          <label class="field"><span>角色名称</span><input v-model.trim="roleForm.roleName"></label>
          <label class="field"><span>状态</span><FancySelect v-model="roleForm.statusCode" :options="roleFormStatusOptions" :disabled="roleFormStatusLocked" /></label>
          <div class="perm-tabs">
            <button type="button" class="perm-tab" :class="{ active: rolePermTab === 'web' }" @click="rolePermTab = 'web'">web后台权限</button>
            <button type="button" class="perm-tab" :class="{ active: rolePermTab === 'mp' }" @click="rolePermTab = 'mp'">小程序权限</button>
          </div>
          <div v-show="rolePermTab === 'web'" class="field field-full">
            <span class="field-title">Web 后台权限（树形）</span>
            <p class="perm-hint perm-hint--mp-tree">按侧栏板块划分；展开模块可勾选查看/编辑/审核等细粒度权限。勾选父节点将同步勾选其下全部子项。</p>
            <div class="mp-tree mp-tree--el">
              <el-tree
                v-if="webPermTree.length"
                ref="webPermTreeRef"
                :key="webTreeInstanceKey"
                class="mp-perm-el-tree"
                :data="webPermTree"
                node-key="code"
                show-checkbox
                :props="webTreeProps"
                :default-expanded-keys="webTreeExpandedKeys"
                :expand-on-click-node="false"
                @check="onWebTreeCheck"
                @node-expand="onWebTreeNodeExpand"
                @node-collapse="onWebTreeNodeCollapse"
              />
              <p v-else class="mp-tree-empty">暂无 Web 后台权限配置项</p>
            </div>
        </div>
          <div v-show="rolePermTab === 'mp'" class="field field-full">
            <span class="field-title">小程序「我的管理」权限（树形）</span>
            <p class="perm-hint perm-hint--mp-tree">点击左侧箭头展开/收起子项；勾选父节点将同步勾选其下全部子权限。</p>
            <div class="mp-tree mp-tree--el">
              <el-tree
                v-if="mpPermTree.length"
                ref="mpPermTreeRef"
                :key="mpTreeInstanceKey"
                class="mp-perm-el-tree"
                :data="mpPermTree"
                node-key="code"
                show-checkbox
                :props="mpTreeProps"
                :default-expanded-keys="mpTreeExpandedKeys"
                :expand-on-click-node="false"
                @check="onMpTreeCheck"
                @node-expand="onMpTreeNodeExpand"
                @node-collapse="onMpTreeNodeCollapse"
              />
              <p v-else class="mp-tree-empty">暂无小程序权限配置项</p>
            </div>
          </div>
        </div>
        </div>
        <div class="dialog-foot">
          <button class="btn btn-cancel" @click="showRoleDialog = false">取消</button>
          <button class="btn btn-primary" :disabled="!canManage" @click="saveRole">保存</button>
        </div>
      </div>
    </div>

    <div v-if="showUserDialog" class="mask">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ userEditId ? '编辑后台用户' : '新增后台用户' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showUserDialog = false">×</button>
        </div>
        <div class="dialog-body">
        <p v-if="userDialogError" class="error">{{ userDialogError }}</p>
        <div class="form">
          <label class="field"><span>账号</span><input v-model.trim="userForm.username" :disabled="!!userEditId" placeholder="登录电脑后台用"></label>
          <label class="field"><span>姓名</span><input v-model.trim="userForm.displayName"></label>
          <template v-if="userEditId && isSuperAdmin">
            <label class="field">
              <span>重置密码</span>
              <input
                v-model="userForm.resetPassword"
                type="password"
                autocomplete="new-password"
                placeholder="可选；无需对方旧密码，6～64 位"
              />
            </label>
            <label class="field">
              <span>确认新密码</span>
              <input
                v-model="userForm.resetPassword2"
                type="password"
                autocomplete="new-password"
                placeholder="再次输入新密码"
              />
            </label>
            <p class="field-hint field-full">仅超级管理员可重置；填写后点「保存」直接生效，请告知对方新密码。</p>
          </template>
          <label class="field field-full">
            <span>绑定手机号</span>
            <input v-model.trim="userForm.bindMobile" maxlength="11" placeholder="职员小程序「我的管理」用，须与会员档案注册手机号一致">
            <small class="field-hint">改会员登录/注册手机号请到「会员管理 → 编辑」，不要在此改 C 端用户档案。</small>
          </label>
          <label class="field field-full field-upload">
            <span>头像</span>
            <input ref="adminAvatarFile" class="hidden-input" type="file" accept="image/*" @change="onAdminAvatarFile">
            <div class="upload-box upload-box--avatar" @click="pickAdminAvatar">
              <img
                v-if="adminAvatarPreview"
                class="upload-preview-img upload-preview-img--avatar"
                :src="adminAvatarPreview"
                alt="avatar"
              />
              <template v-else>
                <div class="upload-plus">+</div>
                <div class="upload-text">选择图片</div>
              </template>
            </div>
            <small v-if="avatarUploadMsg" class="field-hint">{{ avatarUploadMsg }}</small>
          </label>
          <label class="field"><span>邮箱</span><input v-model.trim="userForm.email" placeholder="可选"></label>
          <label class="field"><span>会员专员类型</span><FancySelect v-model="userForm.staffScopeCode" :options="staffScopeOptions" /></label>
          <label class="field field-full field-check">
            <span>人脉群聊管理员</span>
            <label class="inline-check">
              <input v-model="userForm.networkChatAdmin" type="checkbox" />
              <span>审核人脉订单时，可作为群聊参与者被勾选（须已填写绑定手机号且存在对应会员档案）</span>
            </label>
          </label>
          <label class="field"><span>状态</span><FancySelect v-model="userForm.statusCode" :options="userFormStatusOptions" :disabled="userFormStatusLocked" /></label>
          <label class="field"><span>描述</span><input v-model.trim="userForm.remark" placeholder="可选"></label>
          <label v-if="!userEditId" class="field">
            <span>密码</span>
            <input v-model="userForm.password" placeholder="登录电脑后台的密码" />
          </label>
          <label class="field field-full">
            <span>所属角色（多选，权限由角色决定）</span>
            <div class="perm-list">
              <label v-for="r in roles" :key="r.id" class="perm-item">
                <input type="checkbox" :value="r.id" v-model="userForm.roleIds" />
                <span>{{ r.roleName }}（{{ r.roleCode || `ROLE#${r.id}` }}）</span>
              </label>
            </div>
          </label>
            </div>
            </div>

        <div class="dialog-foot">
          <button class="btn btn-cancel" @click="showUserDialog = false">取消</button>
          <button class="btn btn-primary" :disabled="!canManage" @click="saveUser">保存</button>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import { ElTree } from 'element-plus'
import 'element-plus/es/components/tree/style/css'
import FancySelect from '@/components/FancySelect.vue'
import {
  BASE_URL,
  createRbacRole,
  createRbacUser,
  getPermissionCatalog,
  getRbacRoles,
  getRbacUsers,
  getStaffMpPermissionsCatalog,
  getWebPermissionsCatalog,
  resetRbacUserPassword,
  deleteRbacUser,
  updateRbacRole,
  updateRbacUser,
  uploadImage,
} from '@/api'

const newRole = () => ({ roleCode: '', roleName: '', statusCode: 'ENABLED', permissions: [], mpPermissions: [] })
const LEGACY_WEB_PERM_CODES = new Set([
  'cms.manage',
  'cms.view',
  'cms.edit',
  'cms.delete',
  'content.manage',
  'form.manage',
  'member.manage',
  'dict.manage',
  'supply.review',
])
const newUser = () => ({
  username: '',
  displayName: '',
  bindMobile: '',
  email: '',
  avatarUrl: '',
  staffScopeCode: 'ALL_MEMBERS',
  remark: '',
  statusCode: 'ENABLED',
  password: '',
  resetPassword: '',
  resetPassword2: '',
  networkChatAdmin: false,
  roleIds: [],
})

export default {
  name: 'PermissionManageView',
  components: { FancySelect, ElTree },
  data() {
    return {
      errorMsg: '',
      canManage: true,
      isSuperAdmin: false,
      roles: [],
      users: [],
      permissionCatalog: [],
      webPermTree: [],
      statusOptions: [{ value: 'ENABLED', label: '启用' }, { value: 'DISABLED', label: '停用' }],
      statusBusyKey: '',
      staffScopeOptions: [
        { value: 'ALL_MEMBERS', label: '管理员（所有会员）' },
        { value: 'SERVICES_SPECIALIST', label: '专员（仅分配的会员）' },
      ],
      rolePermTab: 'web',
      mpPermTree: [],
      showRoleDialog: false,
      roleEditId: null,
      roleForm: newRole(),
      showUserDialog: false,
      userEditId: null,
      userForm: newUser(),
      userDialogError: '',
      avatarUploadMsg: '',
      avatarPickBusy: false,
      webTreeInstanceKey: 0,
      webTreeProps: { label: 'label', children: 'children' },
      webTreeExpandedKeys: [],
      mpTreeInstanceKey: 0,
      mpTreeProps: { label: 'label', children: 'children' },
      /** 树展开状态（勿用 computed 绑定 default-expanded-keys，否则勾选变化会重置展开） */
      mpTreeExpandedKeys: [],
    }
  },
  watch: {
    rolePermTab(tab) {
      if (!this.showRoleDialog) return
      this.$nextTick(() => {
        if (tab === 'web') this.syncWebPermTreeChecks()
        else if (tab === 'mp') this.syncMpPermTreeChecks()
      })
    },
  },
  computed: {
    adminAvatarPreview() {
      return this.toFullUrl(this.userForm.avatarUrl)
    },
    roleFormStatusLocked() {
      return this.isProtectedRoleCode(this.roleForm.roleCode)
    },
    userFormStatusLocked() {
      return this.isProtectedUsername(this.userForm.username)
    },
    roleFormStatusOptions() {
      if (this.roleFormStatusLocked) {
        return [{ value: 'ENABLED', label: '启用' }]
      }
      return this.statusOptions
    },
    userFormStatusOptions() {
      if (this.userFormStatusLocked) {
        return [{ value: 'ENABLED', label: '启用' }]
      }
      return this.statusOptions
    },
  },
  mounted() {
    this.canManage = this.resolveCanManage()
    this.isSuperAdmin = this.resolveIsSuperAdmin()
    this.loadAll()
  },
  methods: {
    toFullUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u)) return u
      const base = String(BASE_URL || '').replace(/\/$/, '')
      return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`
    },
    pickAdminAvatar() {
      if (this.avatarPickBusy) return
      this.avatarPickBusy = true
      const input = this.$refs.adminAvatarFile
      if (input) input.click()
      window.setTimeout(() => {
        this.avatarPickBusy = false
      }, 300)
    },
    async onAdminAvatarFile(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        this.avatarUploadMsg = '上传中…'
        const data = await uploadImage(file)
        this.userForm.avatarUrl = data && data.url ? String(data.url) : ''
        this.avatarUploadMsg = '头像已上传，请点「保存」'
      } catch (err) {
        this.avatarUploadMsg = ''
        this.errorMsg = err.message || '头像上传失败'
      }
      e.target.value = ''
    },
    resolveCanManage() {
      try {
        const raw = localStorage.getItem('admin.profile') || ''
        const obj = raw ? JSON.parse(raw) : null
        const ps = obj && typeof obj === 'object' ? obj.permissions : null
        const arr = Array.isArray(ps) ? ps : []
        return arr.includes('rbac.manage') || arr.includes('*')
      } catch {
        return false
      }
    },
    resolveIsSuperAdmin() {
      try {
        const raw = localStorage.getItem('admin.profile') || ''
        const obj = raw ? JSON.parse(raw) : null
        const ps = obj && typeof obj === 'object' ? obj.permissions : null
        const arr = Array.isArray(ps) ? ps : []
        if (arr.includes('*')) return true
        const codes = Array.isArray(obj?.roleCodes) ? obj.roleCodes : []
        if (codes.some((c) => String(c || '').trim().toUpperCase() === 'SUPER_ADMIN')) return true
        const names = Array.isArray(obj?.roleNames) ? obj.roleNames : []
        if (names.some((n) => String(n || '').trim() === '超级管理员')) return true
        return false
      } catch {
        return false
      }
    },
    expandLegacyWebPerms(perms) {
      const set = new Set((perms || []).map((x) => String(x || '').trim()).filter(Boolean))
      const add = (...codes) => codes.forEach((c) => set.add(c))
      if (set.has('content.manage') || set.has('cms.manage')) {
        add('content.view', 'content.edit', 'content.delete', 'album.view', 'album.edit', 'album.delete')
      }
      if (set.has('dict.manage')) {
        add('dict.view', 'dict.edit', 'dict.delete')
      }
      if (set.has('form.manage')) {
        add(
          'form.join.view',
          'form.join.edit',
          'form.submissions.view',
          'form.submissions.review',
          'satisfaction.forms.view',
          'satisfaction.forms.edit',
          'satisfaction.forms.delete',
        )
      }
      if (set.has('member.manage')) {
        add(
          'member.view',
          'member.edit',
          'member.delete',
          'member.profiles.view',
          'member.profiles.edit',
          'form.submissions.view',
          'form.submissions.review',
          'network.orders.view',
          'network.orders.review',
          'cooperation.view',
          'cooperation.edit',
          'cooperation.review',
          'template.confirm.view',
          'template.confirm.edit',
          'template.fee.view',
          'template.fee.edit',
          'template.pricing.view',
          'template.pricing.edit',
          'template.badge.view',
          'template.badge.edit',
          'serviceForm.records.view',
          'serviceForm.records.export',
          'serviceForm.config.view',
          'serviceForm.config.edit',
        )
      }
      return [...set]
    },
    bumpWebTreeInstance() {
      this.webTreeInstanceKey += 1
    },
    onWebTreeNodeExpand(data) {
      const code = data && data.code ? String(data.code) : ''
      if (!code || this.webTreeExpandedKeys.includes(code)) return
      this.webTreeExpandedKeys = [...this.webTreeExpandedKeys, code]
    },
    onWebTreeNodeCollapse(data) {
      const code = data && data.code ? String(data.code) : ''
      if (!code) return
      this.webTreeExpandedKeys = this.webTreeExpandedKeys.filter((k) => k !== code)
    },
    syncWebPermTreeChecks() {
      const tree = this.$refs.webPermTreeRef
      if (!tree || typeof tree.setCheckedKeys !== 'function') return
      const keys = this.expandLegacyWebPerms(this.roleForm.permissions || []).filter((c) => !c.startsWith('web.'))
      tree.setCheckedKeys(keys, false)
    },
    onWebTreeCheck() {
      const tree = this.$refs.webPermTreeRef
      if (!tree) return
      const checked = tree.getCheckedKeys(false) || []
      const half = tree.getHalfCheckedKeys() || []
      const leaves = [...new Set([...checked, ...half].map((x) => String(x || '').trim()).filter((c) => c && !c.startsWith('web.')))]
      const legacy = (this.roleForm.permissions || []).filter((c) => LEGACY_WEB_PERM_CODES.has(String(c || '').trim()))
      this.roleForm.permissions = [...new Set([...legacy, ...leaves])]
    },
    async loadAll() {
      try {
        this.errorMsg = ''
        if (!this.canManage) {
          this.roles = []
          this.users = []
          this.permissionCatalog = []
          return
        }
        const [roles, users, perms, webTree, mpTree] = await Promise.all([
          getRbacRoles(),
          getRbacUsers(),
          getPermissionCatalog(),
          getWebPermissionsCatalog().catch(() => []),
          getStaffMpPermissionsCatalog().catch(() => []),
        ])
        this.roles = roles || []
        this.users = users || []
        this.permissionCatalog = perms || []
        this.webPermTree = Array.isArray(webTree) ? webTree : []
        this.mpPermTree = Array.isArray(mpTree) ? mpTree : []
      } catch (e) {
        this.errorMsg = e.message || '读取权限数据失败'
      }
    },
    staffScopeLabel(code) {
      const c = String(code || '').trim()
      if (c === 'SERVICES_SPECIALIST') return '专员'
      return '管理员'
    },
    statusLabel(code) {
      const c = String(code || '').trim().toUpperCase()
      if (c === 'ENABLED') return '启用'
      if (c === 'DISABLED') return '停用'
      return String(code || '').trim() || '—'
    },
    isProtectedRoleCode(code) {
      return String(code || '').trim().toUpperCase() === 'SUPER_ADMIN'
    },
    isProtectedUsername(username) {
      return String(username || '').trim().toLowerCase() === 'admin'
    },
    isProtectedRole(r) {
      return this.isProtectedRoleCode(r?.roleCode)
    },
    isProtectedUser(u) {
      return this.isProtectedUsername(u?.username)
    },
    isTargetSuperAdminAccount(u) {
      if (this.isProtectedUser(u)) return true
      const codes = Array.isArray(u?.roleCodes) ? u.roleCodes : []
      if (codes.some((c) => String(c || '').trim().toUpperCase() === 'SUPER_ADMIN')) return true
      const perms = Array.isArray(u?.effectivePermissions) ? u.effectivePermissions : []
      if (perms.includes('*')) return true
      const names = this.userRoleNames(u)
      if (names.some((n) => String(n || '').trim() === '超级管理员')) return true
      return false
    },
    currentAdminUserId() {
      try {
        const raw = localStorage.getItem('admin.profile') || ''
        const obj = raw ? JSON.parse(raw) : null
        const id = obj && obj.id != null ? Number(obj.id) : 0
        return Number.isFinite(id) ? id : 0
      } catch {
        return 0
      }
    },
    canDeleteUser(u) {
      if (!this.isSuperAdmin) return false
      if (!u || u.id == null) return false
      if (Number(u.id) === this.currentAdminUserId()) return false
      if (this.isTargetSuperAdminAccount(u)) return false
      return true
    },
    deleteUserDisabledReason(u) {
      if (!this.isSuperAdmin) return '仅超级管理员可删除'
      if (Number(u?.id) === this.currentAdminUserId()) return '不能删除当前登录账号'
      if (this.isTargetSuperAdminAccount(u)) return '超管账号不可删除'
      return '删除用户'
    },
    async onDeleteUser(u) {
      if (!this.canDeleteUser(u)) {
        this.errorMsg = this.deleteUserDisabledReason(u)
        return
      }
      const name = String(u.displayName || u.username || '').trim() || '该用户'
      const ok = await adminConfirm(
        `确定删除后台用户「${name}」？\n\n删除后：\n1）该账号将无法登录；\n2）会员管理中服务专员为「${name}」的记录将清空，可重新选择专员。`,
      )
      if (!ok) return
      this.errorMsg = ''
      try {
        const res = await deleteRbacUser(u.id)
        const cleared = Number(res?.clearedServiceConsultantCount || 0)
        await this.loadAll()
        this.errorMsg = ''
        window.alert(
          cleared > 0
            ? `已删除「${name}」，并清空 ${cleared} 名会员的服务专员。`
            : `已删除「${name}」。`,
        )
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    isStatusEnabled(code) {
      return String(code || '').trim().toUpperCase() === 'ENABLED'
    },
    roleStatusSwitchDisabled(r) {
      if (!this.canManage) return true
      if (this.isProtectedRole(r)) return true
      return this.statusBusyKey === `role:${r.id}`
    },
    userStatusSwitchDisabled(u) {
      if (!this.canManage) return true
      if (this.isProtectedUser(u)) return true
      return this.statusBusyKey === `user:${u.id}`
    },
    onRoleStatusChange(r, ev) {
      const enabled = !!(ev && ev.target && ev.target.checked)
      if (ev && ev.target) {
        ev.target.checked = this.isStatusEnabled(r.statusCode)
      }
      void this.setRoleStatus(r, enabled)
    },
    onUserStatusChange(u, ev) {
      const enabled = !!(ev && ev.target && ev.target.checked)
      if (ev && ev.target) {
        ev.target.checked = this.isStatusEnabled(u.statusCode)
      }
      void this.setUserStatus(u, enabled)
    },
    bumpMpTreeInstance() {
      this.mpTreeInstanceKey += 1
    },
    onMpTreeNodeExpand(data) {
      const code = data && data.code ? String(data.code) : ''
      if (!code || this.mpTreeExpandedKeys.includes(code)) return
      this.mpTreeExpandedKeys = [...this.mpTreeExpandedKeys, code]
    },
    onMpTreeNodeCollapse(data) {
      const code = data && data.code ? String(data.code) : ''
      if (!code) return
      this.mpTreeExpandedKeys = this.mpTreeExpandedKeys.filter((k) => k !== code)
    },
    syncMpPermTreeChecks() {
      const tree = this.$refs.mpPermTreeRef
      if (!tree || typeof tree.setCheckedKeys !== 'function') return
      const keys = (this.roleForm.mpPermissions || []).map((x) => String(x || '').trim()).filter(Boolean)
      tree.setCheckedKeys(keys, false)
    },
    onMpTreeCheck() {
      const tree = this.$refs.mpPermTreeRef
      if (!tree) return
      const checked = tree.getCheckedKeys(false) || []
      const half = tree.getHalfCheckedKeys() || []
      const merged = [...new Set([...checked, ...half].map((x) => String(x || '').trim()).filter(Boolean))]
      this.roleForm.mpPermissions = merged
    },
    openRoleCreate() {
      this.roleEditId = null
      this.roleForm = newRole()
      this.webTreeExpandedKeys = []
      this.mpTreeExpandedKeys = []
      this.bumpWebTreeInstance()
      this.bumpMpTreeInstance()
      this.rolePermTab = 'web'
      this.showRoleDialog = true
      this.$nextTick(() => this.syncWebPermTreeChecks())
    },
    openRoleEdit(r) {
      this.roleEditId = r.id
      const mpPermissions = [...(r.mpPermissions || [])]
      const permissions = [...(r.permissions || [])]
      this.roleForm = {
        roleCode: r.roleCode,
        roleName: r.roleName,
        statusCode: r.statusCode,
        permissions,
        mpPermissions,
      }
      this.webTreeExpandedKeys = []
      this.mpTreeExpandedKeys = []
      this.bumpWebTreeInstance()
      this.bumpMpTreeInstance()
      this.rolePermTab = 'web'
      this.showRoleDialog = true
      this.$nextTick(() => {
        this.syncWebPermTreeChecks()
        this.syncMpPermTreeChecks()
      })
    },
    async saveRole() {
      try {
        if (!this.canManage) throw new Error('权限不足：需要 rbac.manage')
        if (!this.roleForm.roleName) throw new Error('角色名称必填')
        if (this.roleFormStatusLocked) {
          this.roleForm.statusCode = 'ENABLED'
        }
        if (this.roleEditId) await updateRbacRole(this.roleEditId, this.roleForm)
        else await createRbacRole(this.roleForm)
        this.showRoleDialog = false
        await this.loadAll()
      } catch (e) {
        this.errorMsg = e.message || '保存角色失败'
      }
    },
    async setRoleStatus(r, enabled) {
      if (!r || r.id == null) return
      if (this.isProtectedRole(r) && !enabled) {
        this.errorMsg = '超级管理员角色不可停用'
        return
      }
      const nextStatus = enabled ? 'ENABLED' : 'DISABLED'
      if (String(r.statusCode || '').toUpperCase() === nextStatus) return
      const key = `role:${r.id}`
      this.statusBusyKey = key
      this.errorMsg = ''
      try {
        if (!this.canManage) throw new Error('权限不足：需要 rbac.manage')
        await updateRbacRole(r.id, {
          roleCode: r.roleCode || '',
          roleName: r.roleName,
          statusCode: nextStatus,
          permissions: [...(r.permissions || [])],
          mpPermissions: [...(r.mpPermissions || [])],
        })
        await this.loadAll()
      } catch (e) {
        this.errorMsg = e.message || '切换角色状态失败'
      } finally {
        if (this.statusBusyKey === key) this.statusBusyKey = ''
      }
    },
    openUserCreate() {
      this.userEditId = null
      this.userForm = newUser()
      this.avatarUploadMsg = ''
      this.userDialogError = ''
      this.errorMsg = ''
      this.showUserDialog = true
    },
    openUserEdit(u) {
      this.isSuperAdmin = this.resolveIsSuperAdmin()
      this.avatarUploadMsg = ''
      this.userDialogError = ''
      this.errorMsg = ''
      this.userEditId = u.id
      this.userForm = {
        username: u.username,
        displayName: u.displayName,
        bindMobile: u.bindMobile || '',
        email: u.email || '',
        avatarUrl: u.avatarUrl || '',
        staffScopeCode: u.staffScopeCode || 'ALL_MEMBERS',
        remark: u.remark || '',
        statusCode: u.statusCode,
        password: '',
        resetPassword: '',
        resetPassword2: '',
        networkChatAdmin: !!u.networkChatAdmin,
        roleIds: this.resolveRoleIds(u),
      }
      this.showUserDialog = true
    },
    resolveRoleIds(u) {
      if (Array.isArray(u?.roleIds) && u.roleIds.length) {
        return [...u.roleIds].map((x) => Number(x)).filter((x) => Number.isFinite(x))
      }
      const roleCodes = new Set([...(u?.roleCodes || [])].map((x) => String(x)))
      return (this.roles || [])
        .filter((r) => roleCodes.has(String(r.roleCode)))
        .map((r) => r.id)
    },
    userRoleNames(u) {
      const ids = new Set(this.resolveRoleIds(u))
      return (this.roles || [])
        .filter((r) => ids.has(r.id))
        .map((r) => r.roleName)
    },
    async saveUser() {
      try {
        this.userDialogError = ''
        if (!this.canManage) throw new Error('权限不足：需要 rbac.manage')
        if (this.userFormStatusLocked) {
          this.userForm.statusCode = 'ENABLED'
        }
        if (!this.userForm.displayName) throw new Error('显示名必填')
        if (!this.userEditId && (!this.userForm.username || !this.userForm.password)) {
          throw new Error('新增用户时，用户名和密码必填')
        }
        const resetPwd = String(this.userForm.resetPassword || '').trim()
        const resetPwd2 = String(this.userForm.resetPassword2 || '').trim()
        if (this.userEditId && (resetPwd || resetPwd2)) {
          if (!this.isSuperAdmin) throw new Error('仅超级管理员可重置他人密码')
          if (!resetPwd || resetPwd.length < 6) throw new Error('新密码至少 6 位')
          if (resetPwd !== resetPwd2) throw new Error('两次输入的新密码不一致')
        }
        if (this.userEditId && this.userForm.password) {
          throw new Error('请使用下方「重置密码」字段（仅超级管理员）')
        }
        if (this.userEditId) {
          const payload = { ...this.userForm, password: '' }
          await updateRbacUser(this.userEditId, payload)
          if (resetPwd) {
            await resetRbacUserPassword(this.userEditId, resetPwd)
          }
        } else {
          await createRbacUser(this.userForm)
        }
        this.showUserDialog = false
        this.userDialogError = ''
        this.errorMsg = ''
        await this.loadAll()
      } catch (e) {
        const msg = e.message || '保存用户失败'
        this.userDialogError = msg
        this.errorMsg = ''
      }
    },
    async setUserStatus(u, enabled) {
      if (!u || u.id == null) return
      if (this.isProtectedUser(u) && !enabled) {
        this.errorMsg = '系统管理员账号不可停用'
        return
      }
      const nextStatus = enabled ? 'ENABLED' : 'DISABLED'
      if (String(u.statusCode || '').toUpperCase() === nextStatus) return
      const key = `user:${u.id}`
      this.statusBusyKey = key
      this.errorMsg = ''
      try {
        if (!this.canManage) throw new Error('权限不足：需要 rbac.manage')
        await updateRbacUser(u.id, {
          displayName: u.displayName,
          bindMobile: u.bindMobile || '',
          email: u.email || '',
          avatarUrl: u.avatarUrl || '',
          staffScopeCode: u.staffScopeCode || 'ALL_MEMBERS',
          remark: u.remark || '',
          networkChatAdmin: !!u.networkChatAdmin,
          statusCode: nextStatus,
          password: '',
          roleIds: this.resolveRoleIds(u),
        })
        await this.loadAll()
      } catch (e) {
        this.errorMsg = e.message || '切换用户状态失败'
      } finally {
        if (this.statusBusyKey === key) this.statusBusyKey = ''
      }
    },
  },
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 16px; }
.head { margin-bottom: 10px; }
.sub { color: #667085; margin: 0; }
.warn { color: #b54708; background: #fffaeb; border: 1px solid #fedf89; padding: 8px 10px; border-radius: 10px; margin: 10px 0; }
.error { color: #d92d20; margin-bottom: 8px; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.panel { border: 1px solid #e9eef5; border-radius: 12px; padding: 12px; background: #fff; }
.panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.panel-head h3 { margin: 0; }
table { width: 100%; border-collapse: collapse; }
.rbac-table th,
.rbac-table td {
  border-bottom: 1px solid #edf0f5;
  padding: 10px 8px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
}
.rbac-table th {
  color: #475467;
  font-weight: 600;
  background: #f9fafb;
}
.col-status { width: 72px; }
.rbac-table .btn + .btn { margin-left: 0; }
.status-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  cursor: pointer;
}
.status-switch.disabled {
  cursor: not-allowed;
  opacity: 0.72;
}
.status-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.status-switch-track {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: #d0d5dd;
  transition: background 0.18s ease;
}
.status-switch-track::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.18);
  transition: transform 0.18s ease;
}
.status-switch.on .status-switch-track {
  background: #2f6af6;
}
.status-switch.on .status-switch-track::after {
  transform: translateX(20px);
}
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 8px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #fecdca; background: #fff; color: #d92d20; }
.btn-delete:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.col-actions { white-space: nowrap; }
.col-actions .btn { margin-right: 6px; }
.btn[disabled] { opacity: 0.55; cursor: not-allowed; }
.form { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; }
.field > span,
.field > .field-title { font-weight: 600; }
.field-title {
  display: block;
  font-size: 13px;
  color: #344054;
  margin-bottom: 2px;
}
.field-full { grid-column: span 2; }
input { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; background: #fff; }
.perm-list { border: 1px solid #e5e7eb; border-radius: 10px; max-height: 220px; overflow: auto; padding: 6px; }
.perm-item { display: flex; align-items: center; gap: 8px; padding: 6px; font-size: 13px; color: #344054; }
.perm-matrix-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: auto;
  margin-bottom: 8px;
}
.perm-matrix th,
.perm-matrix td {
  white-space: nowrap;
  text-align: center;
}
.perm-matrix td:first-child,
.perm-matrix th:first-child {
  text-align: left;
}
.perm-hint {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: normal;
  color: #667085;
  line-height: 1.5;
}
.perm-hint--user {
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 8px 10px;
}
.perm-matrix-toolbar {
  margin-bottom: 6px;
}
.btn-tiny {
  padding: 4px 10px;
  font-size: 12px;
}
.perm-corner,
.perm-row-head {
  cursor: context-menu;
  user-select: none;
}
.perm-ctx-menu {
  position: fixed;
  z-index: 10000;
  min-width: 140px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.12);
  padding: 4px 0;
}
.perm-ctx-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 8px 14px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
}
.perm-ctx-item:hover {
  background: #f2f4f7;
}
.perm-user-cb--roleonly {
  opacity: 0.55;
  cursor: not-allowed;
  accent-color: #98a2b3;
}
.perm-tabs {
  grid-column: span 2;
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.perm-tab {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}
.perm-tab.active {
  border-color: #2f6af6;
  background: #eff4ff;
  color: #175cd3;
}
.perm-hint--mp-tree {
  margin-top: 4px;
}
.mp-tree {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  max-height: 360px;
  overflow: auto;
  padding: 8px 10px;
  background: #fafbfc;
}
.mp-tree--el {
  padding: 4px 6px 8px;
}
.mp-tree-empty {
  margin: 12px 8px;
  font-size: 13px;
  color: #667085;
}
.mp-perm-el-tree {
  background: transparent;
  --el-tree-node-hover-bg-color: #eef2f6;
}
.mp-perm-el-tree :deep(.el-tree-node__content) {
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  color: #344054;
}
.mp-perm-el-tree :deep(.el-tree-node__expand-icon) {
  font-size: 14px;
  color: #667085;
  padding: 6px;
  margin-right: 2px;
}
.mp-perm-el-tree :deep(.el-tree-node__expand-icon.is-leaf) {
  visibility: hidden;
}
.field-hint {
  font-size: 12px;
  font-weight: normal;
  color: #667085;
  line-height: 1.45;
}
.field-check .inline-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #344054;
  line-height: 1.45;
  font-weight: normal;
}
.field-check .inline-check input {
  margin-top: 3px;
}
.hidden-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
.field-upload .upload-box {
  border: 1px dashed #d0d7e2;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  background: #fafbfc;
  max-width: 120px;
}
.field-upload .upload-box--avatar {
  width: 96px;
  height: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.upload-plus {
  font-size: 28px;
  color: #98a2b3;
  line-height: 1;
}
.upload-text {
  font-size: 12px;
  color: #667085;
  margin-top: 4px;
}
.upload-preview-img {
  max-width: 100%;
  max-height: 72px;
  object-fit: contain;
}
.upload-preview-img--avatar {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 8px;
}
</style>
